import { KeyRound, Pencil, Shield, Trash2 } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import { EmptyState } from "@/shared/components/EmptyState";
import { RowActionMenu, type RowAction } from "@/shared/components/RowActionMenu";

type AdminUser = components["schemas"]["AdminUser"];

interface UserTableProps {
  items: AdminUser[];
  isLoading: boolean;
  isError: boolean;
  /** Whether the current user holds `users:manage` — hides the actions column when false. */
  canManage: boolean;
  onEdit: (user: AdminUser) => void;
  onSetRoles: (user: AdminUser) => void;
  onResetPassword: (user: AdminUser) => void;
  onDelete: (user: AdminUser) => void;
}

const TH_CLASS =
  "h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap";
const TD_CLASS = "h-[52px] px-4 text-sm text-[var(--text-secondary)] whitespace-nowrap";

export function UserTable({
  items,
  isLoading,
  isError,
  canManage,
  onEdit,
  onSetRoles,
  onResetPassword,
  onDelete,
}: UserTableProps): JSX.Element {
  const { t } = useTranslation("users");

  if (isLoading) {
    return <div data-testid="user-table-loading">…</div>;
  }
  if (isError) {
    return <div data-testid="user-table-error">{t("error")}</div>;
  }
  if (items.length === 0) {
    return (
      <EmptyState
        title={t("empty.title")}
        body={canManage ? t("empty.body_can_manage") : t("empty.body")}
      />
    );
  }

  return (
    <div
      className="overflow-x-auto rounded-[14px] border border-[var(--premium-border)] shadow-[var(--sh-card)]"
      data-testid="user-table"
    >
      <table className="min-w-full border-collapse">
        <thead className="sticky top-0 z-20 border-b border-[var(--row-divider)] bg-white">
          <tr>
            <th className={TH_CLASS}>{t("column.name")}</th>
            <th className={TH_CLASS}>{t("column.email")}</th>
            <th className={TH_CLASS}>{t("column.roles")}</th>
            <th className={TH_CLASS}>{t("column.created")}</th>
            {canManage && (
              <th className="sticky right-0 z-[6] h-[52px] bg-white px-4">
                <span className="sr-only">{t("column.actions")}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--row-divider)]">
          {items.map((u) => {
            const actions: RowAction[] = [
              {
                key: "edit",
                label: t("action.edit"),
                icon: <Pencil className="h-4 w-4" />,
                onSelect: () => onEdit(u),
              },
              {
                key: "roles",
                label: t("action.set_roles"),
                icon: <Shield className="h-4 w-4" />,
                onSelect: () => onSetRoles(u),
              },
              {
                key: "reset",
                label: t("action.reset_password"),
                icon: <KeyRound className="h-4 w-4" />,
                onSelect: () => onResetPassword(u),
              },
              {
                key: "delete",
                label: t("action.delete"),
                icon: <Trash2 className="h-4 w-4" />,
                danger: true,
                onSelect: () => onDelete(u),
              },
            ];
            return (
              <tr
                key={u.id}
                className="transition-colors hover:bg-[var(--row-hover)]"
                data-testid={`user-row-${u.id}`}
              >
                <td className={`${TD_CLASS} font-medium text-[var(--text-primary)]`}>
                  {u.display_name}
                </td>
                <td className={TD_CLASS}>{u.email}</td>
                <td className={TD_CLASS}>
                  {u.roles.map((r) => t(`role.${r}`, { defaultValue: r })).join(", ")}
                </td>
                <td className={TD_CLASS}>{new Date(u.created_at).toLocaleDateString()}</td>
                {canManage && (
                  <td
                    className="sticky right-0 z-[5] h-[52px] whitespace-nowrap bg-white px-2 shadow-[-10px_0_12px_-10px_rgba(16,24,40,0.16)]"
                    data-testid={`user-row-actions-${u.id}`}
                  >
                    <RowActionMenu actions={actions} label={t("column.actions")} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
